import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BrandShield } from "@/components/BrandLogo";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 12; // ~24s total
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | expired | error
  const [error, setError] = useState("");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) {
      setState("error");
      setError("Missing checkout session.");
      return;
    }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("success");
        refresh?.();
        return;
      }
      if (data.status === "expired") {
        setState("expired");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("error");
        setError("Still processing. Check your email for a receipt, or refresh in a minute.");
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    } catch (e) {
      setState("error");
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not verify payment.");
    }
  }, [sessionId, refresh]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "var(--vault-app-bg)" }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="vault-card max-w-md w-full p-8 text-center"
        data-testid="checkout-success-card"
      >
        <div className="flex justify-center mb-3">
          <BrandShield className="w-14 h-14" />
        </div>

        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mt-2 mb-3 animate-spin text-[color:var(--vault-brand)]" size={32} />
            <h1 className="font-[Outfit] text-2xl font-semibold">Confirming your payment…</h1>
            <p className="text-sm text-[color:var(--vault-text-secondary)] mt-2">
              This usually takes just a moment.
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto mt-2 mb-3 text-[color:var(--vault-success)]" size={36} />
            <h1 className="font-[Outfit] text-2xl font-semibold" data-testid="checkout-success-title">You&apos;re in.</h1>
            <p className="text-sm text-[color:var(--vault-text-secondary)] mt-2">
              Welcome to EZGuard. Your vault is ready, and all future updates are on us.
            </p>
            <Button asChild className="mt-6 w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="checkout-success-cta">
              <Link to="/vault">
                Open my vault <ArrowRight size={16} className="ml-2" />
              </Link>
            </Button>
          </>
        )}

        {state === "expired" && (
          <>
            <XCircle className="mx-auto mt-2 mb-3 text-[color:var(--vault-warning)]" size={36} />
            <h1 className="font-[Outfit] text-2xl font-semibold">Session expired</h1>
            <p className="text-sm text-[color:var(--vault-text-secondary)] mt-2">
              The checkout session expired. No charge was made.
            </p>
            <Button asChild className="mt-6 w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white">
              <Link to="/">Try again</Link>
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="mx-auto mt-2 mb-3 text-[color:var(--vault-error)]" size={36} />
            <h1 className="font-[Outfit] text-2xl font-semibold">Hmm, that didn&apos;t go through</h1>
            <p className="text-sm text-[color:var(--vault-text-secondary)] mt-2">
              {error || "We couldn't confirm your payment. Please try again."}
            </p>
            <div className="mt-6 flex gap-2">
              <Button asChild variant="outline" className="rounded-xl flex-1">
                <Link to="/">Back to home</Link>
              </Button>
              <Button asChild className="rounded-xl flex-1 bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white">
                <Link to="/vault">Open vault</Link>
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
